import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Box, Avatar, Badge, ButtonBase, IconButton, Divider, Menu, MenuItem, Typography, Tooltip } from '@mui/material';
import { Building2, Settings, BotMessageSquare, LogOut, Sun, Moon, PanelLeftClose, Search, Activity } from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { useSignOut, useTenant, usePendingApprovalCount } from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { GlobalSearchDialog } from '../GlobalSearchDialog';
import { UpdateNotificationButton } from '../UpdateNotificationButton';

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
}

const ICON_SIZE = ICON_SIZES.lg;
const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const SEARCH_SHORTCUT = IS_MAC ? '⌘K' : 'Ctrl K';

// Navigation items by role; portco users get no sidebar (see Layout), and Settings sits at the bottom for everyone
const navigationByRole: Record<string, NavItem[]> = {
  super_admin: [
    {
      label: 'Tenants',
      path: '/super-admin/tenants',
      icon: <Building2 size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'Chief of Staff',
      path: '/dashboard',
      icon: <BotMessageSquare size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'Activity',
      path: '/activity',
      icon: <Activity size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
  ],
  admin: [
    {
      label: 'Chief of Staff',
      path: '/dashboard',
      icon: <BotMessageSquare size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'Activity',
      path: '/activity',
      icon: <Activity size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
  ],
};

/** Whether `pathname` is the item's page or one under it, so /settings/billing-success keeps Settings lit. */
export function isActivePath(path: string, pathname: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

interface NavButtonProps {
  label: string;
  icon: ReactNode;
  active: boolean;
  expanded: boolean;
  onClick: () => void;
  /** A count beside the label, or on the icon when collapsed; nothing shows for 0. */
  count?: number;
  countLabel?: string;
}

function NavButton({ label, icon, active, expanded, onClick, count = 0, countLabel }: NavButtonProps) {
  return (
    <ListItem disablePadding sx={{ px: 1 }}>
      <Tooltip title={expanded ? '' : label} placement="right">
        <ListItemButton
          onClick={onClick}
          aria-current={active ? 'page' : undefined}
          sx={{
            borderRadius: 1,
            minHeight: 40,
            px: 1.5,
            justifyContent: expanded ? 'initial' : 'center',
            bgcolor: active ? 'primary.main' : 'transparent',
            color: active ? 'primary.contrastText' : 'text.primary',
            '&:hover': {
              bgcolor: active ? 'primary.dark' : 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 1.5 : 0, justifyContent: 'center', color: 'inherit' }}>
            {expanded ? (
              icon
            ) : (
              <Badge badgeContent={count} color="warning">
                {icon}
              </Badge>
            )}
          </ListItemIcon>
          {expanded && <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />}
          {expanded && count > 0 && (
            <Box
              component="span"
              aria-label={countLabel}
              sx={{ ml: 1, px: 0.75, minWidth: 20, borderRadius: 10, bgcolor: 'warning.main', color: 'warning.contrastText', fontSize: '0.75rem', fontWeight: 600, textAlign: 'center' }}
            >
              {count}
            </Box>
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const signOut = useSignOut();
  const { data: tenant } = useTenant(user?.tenant_id ?? '');

  const { sidebarCollapsed, toggleSidebar, themeMode, toggleTheme } = useUIStore();

  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [accountMenu, setAccountMenu] = useState<HTMLElement | null>(null);

  const expanded = !sidebarCollapsed;
  // The Chief of Staff works in the user's own tenant, so a super admin who belongs to none has no Chief of Staff
  const navItems = (user?.role ? navigationByRole[user.role] || [] : []).filter((item) => item.path !== '/dashboard' || !!user?.tenant_id);
  const isAdmin = user?.role === 'admin';
  const pendingApprovals = usePendingApprovalCount(isAdmin);
  const workspaceName = user?.role !== 'super_admin' && tenant?.name ? tenant.name : 'Diggens';

  // Cmd+K on a Mac, Ctrl+K elsewhere, opens search from anywhere in the app
  useEffect(() => {
    if (!isAdmin) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const modifier = IS_MAC ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
      if (modifier && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchDialogOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isAdmin]);

  const handleSignOut = async () => {
    setAccountMenu(null);
    await signOut.mutateAsync();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return '?';
  };

  const width = expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          transition: 'width 200ms',
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Workspace, and the collapse toggle; collapsed, the logo expands it */}
        <Box sx={{ px: expanded ? 2 : 1, display: 'flex', alignItems: 'center', justifyContent: expanded ? 'space-between' : 'center', minHeight: 64 }}>
          {expanded ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <img src="/logo.svg" alt="Diggens" style={{ height: '24px', width: 'auto' }} />
                <Typography variant="subtitle1" noWrap title={workspaceName} sx={{ fontWeight: 600 }}>
                  {workspaceName}
                </Typography>
              </Box>
              <Tooltip title="Collapse sidebar" placement="right">
                <IconButton size="small" aria-label="Collapse sidebar" onClick={toggleSidebar}>
                  <PanelLeftClose size={ICON_SIZE} strokeWidth={ICON_STROKE} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Expand sidebar" placement="right">
              <ButtonBase aria-label="Expand sidebar" onClick={toggleSidebar} sx={{ p: 1, borderRadius: 1 }}>
                <img src="/logo.svg" alt="" style={{ height: '24px', width: 'auto' }} />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>

        {/* Search - Admin Only */}
        {isAdmin &&
          (expanded ? (
            <ButtonBase
              onClick={() => setSearchDialogOpen(true)}
              sx={{ mx: 2, mb: 1, px: 1.25, py: 0.75, gap: 1, justifyContent: 'flex-start', border: 1, borderColor: 'divider', borderRadius: 1, color: 'text.secondary' }}
            >
              <Search size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
              <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                Search
              </Typography>
              <Typography variant="caption">{SEARCH_SHORTCUT}</Typography>
            </ButtonBase>
          ) : (
            <List sx={{ py: 0 }}>
              <NavButton label="Search" icon={<Search size={ICON_SIZE} strokeWidth={ICON_STROKE} />} active={false} expanded={false} onClick={() => setSearchDialogOpen(true)} />
            </List>
          ))}

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <List sx={{ pt: 0.5 }}>
            {navItems.map((item) => (
              <NavButton
                key={item.path}
                label={item.label}
                icon={item.icon}
                active={isActivePath(item.path, location.pathname)}
                expanded={expanded}
                onClick={() => navigate(item.path)}
                count={item.path === '/activity' ? pendingApprovals : 0}
                countLabel={`${pendingApprovals} plan${pendingApprovals === 1 ? '' : 's'} waiting for approval`}
              />
            ))}
          </List>
        </Box>

        <Divider />

        {/* Update Notification */}
        <UpdateNotificationButton />

        <List sx={{ py: 0.5 }}>
          <NavButton
            label="Settings"
            icon={<Settings size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
            active={isActivePath('/settings', location.pathname)}
            expanded={expanded}
            onClick={() => navigate('/settings')}
          />
        </List>

        {/* Account: theme and sign out live in its menu */}
        <Tooltip title={expanded ? '' : 'Account'} placement="right">
          <ButtonBase
            aria-label="Account menu"
            aria-haspopup="menu"
            onClick={(event) => setAccountMenu(event.currentTarget)}
            sx={{ mx: 1, mb: 1, p: 1, gap: 1.5, borderRadius: 1, justifyContent: expanded ? 'flex-start' : 'center', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>{getUserInitials()}</Avatar>
            {expanded && (
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Typography variant="subtitle2" noWrap>
                  {user?.full_name || user?.email}
                </Typography>
                {user?.full_name && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {user.email}
                  </Typography>
                )}
              </Box>
            )}
          </ButtonBase>
        </Tooltip>
        <Menu
          anchorEl={accountMenu}
          open={accountMenu !== null}
          onClose={() => setAccountMenu(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => {
              toggleTheme();
              setAccountMenu(null);
            }}
          >
            <ListItemIcon>{themeMode === 'light' ? <Moon size={ICON_SIZES.md} strokeWidth={ICON_STROKE} /> : <Sun size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />}</ListItemIcon>
            {themeMode === 'light' ? 'Dark mode' : 'Light mode'}
          </MenuItem>
          <MenuItem onClick={handleSignOut} disabled={signOut.isPending}>
            <ListItemIcon>
              <LogOut size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Box>

      {/* Global Search Dialog */}
      {isAdmin && user?.tenant_id && <GlobalSearchDialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} tenantId={user.tenant_id} />}
    </Drawer>
  );
}
